import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Check, MessageCircle, XCircle } from "lucide-react";
import { useConfig } from "../lib/config";
import SEO from "../components/SEO";
import { isInStock } from "../lib/stock";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");
  const cfg = useConfig();

  useEffect(() => {
    setProduct(null);
    setError("");
    api
      .get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setError("Ürün bulunamadı"));
  }, [slug]);

  if (error)
    return (
      <div className="max-w-7xl mx-auto px-6 py-32 text-center">
        <p className="text-gray-500 mb-6">{error}</p>
        <Link to="/urunler" className="underline">Ürünlere dön</Link>
      </div>
    );

  if (!product) return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-gray-500">Yükleniyor...</div>;

  const waLink = cfg.whatsapp
    ? `https://wa.me/${cfg.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Merhaba, ${product.name} ürünü hakkında bilgi almak istiyorum.`)}`
    : "#";

  return (
    <div data-testid={`product-detail-${product.slug}`}>
      <SEO
        title={product.name}
        description={product.short_description}
        keywords={`${product.name}, fardriver ${product.slug}, ${product.category} kontrolör, elektrikli motor beyin, foc kontrolör, ${product.specs?.["Pik Akım"] || ""}`}
        path={`/urunler/${product.slug}`}
        image={product.image_url?.startsWith("/") ? `https://fardriverturkiye.com${product.image_url}` : product.image_url}
        type="product"
        product={product}
      />
      <section className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Link
            to="/urunler"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-gray-500 hover:text-black"
          >
            <ArrowLeft size={14} /> Ürünlere dön
          </Link>
        </div>
      </section>

      <section className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="border border-gray-200 bg-gray-50 aspect-[4/3] flex items-center justify-center overflow-hidden p-4 sm:p-6">
              <img
                src={product.image_url}
                alt={product.name}
                loading="eager"
                width="800"
                height="600"
                className="max-w-full max-h-full w-auto h-auto object-contain"
              />
            </div>
          </div>

          <div className="lg:col-span-5">
            <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-black leading-tight">
              {product.name}
            </h1>
            <p className="mt-5 text-gray-700 leading-relaxed">{product.description}</p>

            <div className="mt-8 flex flex-wrap gap-3" data-testid="product-actions">
              {isInStock(product.slug) ? (
                <>
                  <div
                    data-testid="product-stock-in"
                    className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em]"
                  >
                    <span className="relative flex h-2.5 w-2.5 items-center justify-center">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                      <span
                        className="relative inline-block h-2.5 w-2.5 rounded-full bg-emerald-500"
                        style={{ boxShadow: "0 0 8px rgba(16,185,129,0.95)" }}
                      />
                    </span>
                    Stokta Var
                  </div>
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="product-whatsapp-btn"
                    className="inline-flex items-center gap-2 bg-[#059669] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-emerald-700"
                  >
                    <MessageCircle size={16} /> WhatsApp ile Sipariş Ver
                  </a>
                </>
              ) : (
                <>
                  <div
                    data-testid="product-stock-out"
                    className="inline-flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em]"
                  >
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full bg-red-500"
                      style={{ boxShadow: "0 0 6px rgba(239,68,68,0.7)" }}
                    />
                    <XCircle size={14} /> Stokta Yok
                  </div>
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="product-whatsapp-btn"
                    className="inline-flex items-center gap-2 border border-black px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-black hover:text-white"
                  >
                    <MessageCircle size={16} /> WhatsApp'tan Bilgi Al
                  </a>
                </>
              )}
            </div>

            {product.features?.length > 0 && (
              <div className="mt-10">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#059669] mb-4">Özellikler</p>
                <ul className="space-y-2">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Specs */}
      {product.specs && Object.keys(product.specs).length > 0 && (
        <section className="border-b border-gray-200 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#059669] mb-4">Teknik Özellikler</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-tighter font-black mb-8">Spec sheet</h2>
            <div className="border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(product.specs).map(([k, v], i) => (
                    <tr key={k} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-6 py-4 font-mono-fd text-xs uppercase tracking-[0.15em] text-gray-500 border-b border-gray-100 w-1/3">
                        {k}
                      </td>
                      <td className="px-6 py-4 font-display tracking-tight border-b border-gray-100">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
